/* -- coding: utf-8 -- */
import React, { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { sanitizeRichText } from '../utils/richText';

const ensureParagraph = (value) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^\s*<\/?(ul|ol|li)/i.test(trimmed)) return trimmed;
  if (/^\s*<p[>\s]/i.test(trimmed)) return trimmed;
  return `<p>${trimmed}</p>`;
};

const CKEDITOR_SCRIPT_SELECTOR = 'script[data-ckeditor-script]';

const waitForClassicEditor = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('window is not available'));
  }

  if (window.ClassicEditor) {
    return Promise.resolve(window.ClassicEditor);
  }

  const script = document.querySelector(CKEDITOR_SCRIPT_SELECTOR);
  if (!script) {
    return Promise.reject(new Error('CKEditor script tag is missing'));
  }

  return new Promise((resolve, reject) => {
    const handleLoad = () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
      if (window.ClassicEditor) {
        resolve(window.ClassicEditor);
      } else {
        reject(new Error('CKEditor failed to expose ClassicEditor'));
      }
    };

    const handleError = () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
      reject(new Error('CKEditor script failed to load'));
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);
  });
};

export default function RichTextEditor({ value, onChange, placeholder = 'Açıklama', minCollapsedHeight = 140 }) {
  const editorContainerRef = useRef(null);
  const editorInstanceRef = useRef(null);
  const lastSentValueRef = useRef('');
  const initialValueRef = useRef(ensureParagraph(sanitizeRichText(value || '')));
  const onChangeRef = useRef(onChange);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        const ClassicEditor = await waitForClassicEditor();
        if (!isMounted || !editorContainerRef.current) {
          return;
        }

        const instance = await ClassicEditor.create(editorContainerRef.current, {
          placeholder,
          language: 'tr',
          toolbar: ['undo', 'redo', '|', 'bold', 'italic', '|', 'bulletedList', 'numberedList'],
        });

        if (!isMounted) {
          await instance.destroy();
          return;
        }

        editorInstanceRef.current = instance;
        setIsReady(true);

        const initialValue = initialValueRef.current;
        lastSentValueRef.current = initialValue;
        if (initialValue) {
          instance.setData(initialValue);
        } else {
          instance.setData('');
        }

        instance.model.document.on('change:data', () => {
          if (!editorInstanceRef.current) return;
          const data = ensureParagraph(sanitizeRichText(editorInstanceRef.current.getData()));
          lastSentValueRef.current = data;
          if (onChangeRef.current) {
            onChangeRef.current(data);
          }
        });
      } catch (err) {
        if (isMounted) {
          setError(err);
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
      if (editorInstanceRef.current) {
        editorInstanceRef.current.destroy().catch(() => {});
        editorInstanceRef.current = null;
      }
      setIsReady(false);
    };
  }, [placeholder]);

  useEffect(() => {
    const sanitized = ensureParagraph(sanitizeRichText(value || ''));
    if (!editorInstanceRef.current) {
      initialValueRef.current = sanitized;
      lastSentValueRef.current = sanitized;
      return;
    }
    if (sanitized === lastSentValueRef.current) return;
    lastSentValueRef.current = sanitized;
    editorInstanceRef.current.setData(sanitized || '');
  }, [value]);

  const minHeight = minCollapsedHeight;

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          '& .ck.ck-editor__editable_inline': {
            minHeight,
            padding: '16px',
          },
          '& .ck.ck-editor__main > .ck-editor__editable:not(.ck-focused)': {
            borderColor: 'divider',
          },
          '& .ck.ck-editor .ck.ck-toolbar': {
            backgroundColor: 'grey.50',
            borderColor: 'divider',
            borderTopLeftRadius: (theme) => `${theme.shape.borderRadius}px`,
            borderTopRightRadius: (theme) => `${theme.shape.borderRadius}px`,
          },
          '& .ck.ck-editor__main': {
            borderBottomLeftRadius: (theme) => `${theme.shape.borderRadius}px`,
            borderBottomRightRadius: (theme) => `${theme.shape.borderRadius}px`,
          },
          '& .ck.ck-editor__main > .ck-editor__editable': {
            backgroundColor: 'background.paper',
            borderBottomLeftRadius: (theme) => `${theme.shape.borderRadius}px`,
            borderBottomRightRadius: (theme) => `${theme.shape.borderRadius}px`,
          },
        }}
      >
        <div ref={editorContainerRef} />
        {!isReady && !error && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.paper',
              borderRadius: 2,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Zengin metin editörü yüklenirken bir sorun oluştu. Lütfen sayfayı yenilemeyi deneyin.
        </Alert>
      )}
    </Box>
  );
}
